// The command as a list, in the order the text has it, and the edits the builder makes to that list. Every entry
// keeps the spelling it was read with, so a command written in short flags stays written in short flags.

import { defaultFlag, findLong, type OptionSpec } from "./options";
import type { Shell } from "./shell";

export type Entry = UrlEntry | OptionEntry | UnknownEntry;

export interface UrlEntry {
  kind: "url";
  value: string;
  // Null where the URL was an operand, which is how curl commands are almost always written.
  flag: string | null;
  variables?: string[];
}

export interface OptionEntry {
  kind: "option";
  // The long spelling, which is what the table is keyed by; `flag` is how this one was written.
  name: string;
  flag: string;
  value: string;
  // The references in the value its shell would expand, set only where there are any, which is seldom.
  variables?: string[];
}

// An option no version of this page knows, kept where it stood so that editing the command never drops it.
export interface UnknownEntry {
  kind: "unknown";
  flag: string;
}

export interface Command {
  entries: Entry[];
  error: string | null;
  // The shell the command was written for, which is the one it is written back for.
  shell: Shell;
}

// A place in the list, carried alongside the entry because that index is what an edit names.
export interface Slot<T extends Entry> {
  index: number;
  entry: T;
}

export interface Block {
  spec: OptionSpec;
  slots: Slot<OptionEntry>[];
}

// The four shapes the builder draws, which is what decides the order of the page: the fields, then the fieldsets an
// option curl reads more than one of, then the flags that have no value to show, then whatever was not recognised.
export interface Arrangement {
  urls: Slot<UrlEntry>[];
  singles: Block[];
  groups: Block[];
  flags: Block[];
  unknown: Slot<UnknownEntry>[];
}

export function arrange(entries: Entry[]): Arrangement {
  const urls: Slot<UrlEntry>[] = [];
  const unknown: Slot<UnknownEntry>[] = [];
  // Insertion-ordered, so a block stands where the first of its option stood in the command.
  const blocks = new Map<string, Block>();

  entries.forEach((entry, index) => {
    if (entry.kind === "url") {
      urls.push({ index, entry });
      return;
    }

    if (entry.kind === "unknown") {
      unknown.push({ index, entry });
      return;
    }

    const spec = findLong(entry.name);
    if (!spec) return;

    const block = blocks.get(entry.name) ?? { spec, slots: [] };
    block.slots.push({ index, entry });
    blocks.set(entry.name, block);
  });

  const present = [...blocks.values()];

  return {
    urls,
    singles: present.filter((block) => block.spec.value !== "none" && !block.spec.repeatable),
    groups: present.filter((block) => block.spec.value !== "none" && block.spec.repeatable),
    flags: present.filter((block) => block.spec.value === "none"),
    unknown,
  };
}

export function optionNames(entries: Entry[]): Set<string> {
  return new Set(entries.filter((entry) => entry.kind === "option").map((entry) => entry.name));
}

export function setValue(entries: Entry[], index: number, value: string): Entry[] {
  return entries.map((entry, at) => at === index && entry.kind !== "unknown" ? { ...entry, value } : entry);
}

export function removeAt(entries: Entry[], index: number): Entry[] {
  return entries.filter((_, at) => at !== index);
}

// After the last URL there already is, so the fieldset reads in the order the command carries them.
export function addUrl(entries: Entry[]): Entry[] {
  return insertAfter(entries, lastIndex(entries, (entry) => entry.kind === "url"), {
    kind: "url",
    value: "",
    flag: null,
  });
}

// Beside the others of its own option where there are any, so the text reads the way the fieldset does; failing that
// a single-letter flag lands beside the last of those, which is what puts it in the bundle rather than after it.
export function addOption(entries: Entry[], spec: OptionSpec): Entry[] {
  const entry: OptionEntry = { kind: "option", name: spec.name, flag: defaultFlag(spec), value: "" };

  const own = lastIndex(entries, (held) => held.kind === "option" && held.name === spec.name);
  if (own >= 0) return insertAfter(entries, own, entry);

  if (bundledLetter(entry) === null) return [...entries, entry];
  return insertAfter(entries, lastIndex(entries, (held) => bundledLetter(held) !== null), entry);
}

// The letter a flag joins a bundle as, or null for anything that has to stand as a word of its own. Only a flag with
// no value can be anywhere but last in a bundle, and only the spelling the command actually used is bundled: an
// option written out as `--silent` is not a letter, whatever its short form happens to be.
export function bundledLetter(entry: Entry): string | null {
  if (entry.kind === "url") return null;
  if (entry.kind === "option" && findLong(entry.name)?.value !== "none") return null;
  return LETTER.test(entry.flag) ? entry.flag.slice(1) : null;
}

const LETTER = /^-[^-]$/;

function insertAfter(entries: Entry[], at: number, entry: Entry): Entry[] {
  if (at < 0) return [...entries, entry];
  return [...entries.slice(0, at + 1), entry, ...entries.slice(at + 1)];
}

function lastIndex(entries: Entry[], matches: (entry: Entry) => boolean): number {
  for (let at = entries.length - 1; at >= 0; at -= 1) {
    if (matches(entries[at])) return at;
  }
  return -1;
}
